import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

export const Lightbox = ({ pics, start, title, onClose }) => {
  const [ref, api] = useEmblaCarousel({ loop: pics.length > 1, startIndex: start });
  const [at, setAt] = useState(start);
  useEffect(() => {
    if (!api) {
      return;
    }
    const on = () => setAt(api.selectedScrollSnap());
    api.on("select", on);
    return () => { api.off("select", on); };
  }, [api]);
  useEffect(() => {
    const key = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") api?.scrollPrev();
      if (e.key === "ArrowRight") api?.scrollNext();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", key);
    return () => { window.removeEventListener("keydown", key); document.body.style.overflow = prev; };
  }, [api, onClose]);
  const nav = "absolute top-1/2 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white backdrop-blur-md hover:bg-white/20";
  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-shade" role="dialog" aria-modal="true" aria-label={`${title} photos`} data-testid="lightbox">
      <div className="flex items-center justify-between px-5 py-4 text-white">
        <p className="font-serif text-lg">{title} <span className="ml-2 font-mono text-xs text-white/60" data-testid="lightbox-count">{at + 1}/{pics.length}</span></p>
        <button type="button" onClick={onClose} className="grid h-10 w-10 place-items-center rounded-full bg-white/10 hover:bg-white/20" aria-label="Close" data-testid="lightbox-close"><X className="h-5 w-5" /></button>
      </div>
      <div className="relative min-h-0 flex-1">
        <div ref={ref} className="h-full overflow-hidden">
          <div className="flex h-full">
            {pics.map((p, i) => (
              <div key={p} className="flex h-full min-w-0 flex-[0_0_100%] items-center justify-center px-2 sm:px-16">
                <img src={p} alt={`${title} photo ${i + 1}`} className="max-h-full max-w-full select-none object-contain" draggable={false} />
              </div>
            ))}
          </div>
        </div>
        {pics.length > 1 && <>
          <button type="button" className={`${nav} left-4`} onClick={() => api?.scrollPrev()} aria-label="Previous photo" data-testid="lightbox-prev"><ChevronLeft className="h-5 w-5" /></button>
          <button type="button" className={`${nav} right-4`} onClick={() => api?.scrollNext()} aria-label="Next photo" data-testid="lightbox-next"><ChevronRight className="h-5 w-5" /></button>
        </>}
      </div>
      <div className="flex justify-center gap-2 overflow-x-auto px-4 py-4">
        {pics.map((p, i) => (
          <button key={p} type="button" onClick={() => api?.scrollTo(i)} data-testid={`lightbox-thumb-${i}`}
            className={`h-12 w-16 shrink-0 overflow-hidden rounded-md transition-opacity ${i === at ? "opacity-100 ring-2 ring-white" : "opacity-50"}`}>
            <img src={p} alt="" className="h-full w-full object-cover" />
          </button>
        ))}
      </div>
    </div>,
    document.body,
  );
};
